import type { StandardCatalog, StandardObservation } from "./kdStandardTypes";
import type {
  AttachmentRead,
  ChatRequest,
  ChatResponse,
  ConversationAccess,
  ConversationCreate,
  ConversationMemberRead,
  ConversationRead,
  ConversationUpdate,
  DirectoryUserRead,
  FeedbackCreate,
  IngestionIssueRead,
  IngestionJobRead,
  IngestionUploadResponse,
  KnowledgeCardDetail,
  KnowledgeCardDraftCreate,
  KnowledgeCardDraftRead,
  KnowledgeCardRemovalRead,
  KnowledgeGraphRead,
  KnowledgeCardSummary,
  KnowledgeSnapshotRead,
  Page,
  RetrievalDebugResponse,
  RetrievalProfileCreate,
  RetrievalProfileDetailRead,
  RetrievalProfileRead,
  RetrievalProfileUpdate,
  RetrievalRunRead,
  RoleRead,
  SessionAuthResponse,
  StatusResponse,
  UserAdminRead,
  UserRead,
  UUID,
} from "./types";
import { ApiClient } from "./client";
import type { MaterialCatalogReview, MaterialVersion } from "./kdMaterialTypes";
import type { ReviewSource, SourceMetadata } from "./kdSourceTypes";
import type { TreeGraph, TreeRun } from "./documentTreeTypes";
import type { CatalogHit, DocumentAnalysisRead, DocumentAnalysisRow, DocumentAnalysisRelation } from "./documentTypes";
import type { KDNormativeConfig, KDPackage, KDReference, KDReviewStatus, KDRun } from "./kdTypes";
import type { ElmaContext, ElmaOptions } from "./kdElmaTypes";

import type { NormativeImport, NormativeCatalog, NormativeSources, NormativeCard, NormativeDetail, NormativeReview, NormativeReviewCreate, NormativeRelease } from "./normativeTypes";

export class AssistantApi {
  constructor(public readonly client: ApiClient) {}

  buildDocumentTree(designation: string): Promise<TreeRun> {
    return this.client.request("/documents/trees", { method: "POST", body: { designation } });
  }
  documentTreeHistory(designation: string, offset = 0): Promise<Page<TreeRun>> {
    return this.client.request("/documents/trees", { query: { designation, limit: 10, offset } });
  }
  getDocumentTree(treeId: string): Promise<TreeRun> {
    return this.client.request(`/documents/trees/${treeId}`);
  }
  getDocumentTreeGraph(treeId: string): Promise<TreeGraph> {
    return this.client.request(`/documents/trees/${treeId}/graph`);
  }
  rebuildDocumentTree(treeId: string): Promise<TreeRun> {
    return this.client.request(`/documents/trees/${treeId}/rebuild`, { method: "POST" });
  }

  searchDocuments(designation: string): Promise<Page<CatalogHit>> {
    return this.client.request("/documents/search", { query: { designation, limit: 30 } });
  }
  startDocumentAnalysis(fileId: string, force = false): Promise<DocumentAnalysisRead> {
    return this.client.request(`/documents/files/${fileId}/analyses`, { method: "POST", body: { force, use_llm: false } });
  }
  documentAnalysisHistory(fileId: string): Promise<Page<DocumentAnalysisRead>> {
    return this.client.request(`/documents/files/${fileId}/analyses`, { query: { limit: 1 } });
  }
  getDocumentAnalysis(analysisId: string): Promise<DocumentAnalysisRead> {
    return this.client.request(`/documents/analyses/${analysisId}`);
  }
  getDocumentAnalysisRows(analysisId: string, offset = 0): Promise<Page<DocumentAnalysisRow>> {
    return this.client.request(`/documents/analyses/${analysisId}/rows`, { query: { limit: 30, offset } });
  }
  getDocumentAnalysisRelations(analysisId: string, offset = 0): Promise<Page<DocumentAnalysisRelation>> {
    return this.client.request(`/documents/analyses/${analysisId}/relations`, { query: { limit: 30, offset } });
  }
  downloadDocumentAnalysisPdf(analysisId: string) {
    return this.client.download(`/documents/analyses/${analysisId}/source.pdf`);
  }

  importNormative(snapshotId: string, file: File): Promise<NormativeImport> { const form = new FormData(); form.append("file", file); return this.client.request(`/kd/normative/${snapshotId}/imports`, { method: "POST", body: form }); }
  getStandardCatalog(): Promise<StandardCatalog> { return this.client.request("/kd/standards"); }
  collectStandards(): Promise<StandardCatalog> { return this.client.request("/kd/standards/collect", { method: "POST" }); }
  refreshStandards(): Promise<{ scheduled: number }> { return this.client.request("/kd/standards/refresh", { method: "POST" }); }
  refreshStandard(id: string): Promise<{ scheduled: number }> { return this.client.request(`/kd/standards/${id}/refresh`, { method: "POST" }); }
  getStandardHistory(id: string): Promise<StandardObservation[]> { return this.client.request(`/kd/standards/${id}/history`); }
  addStandard(designation: string, note: string): Promise<StandardCatalog> { return this.client.request("/kd/standards", { method: "POST", body: { designation, note } }); }
  exportStandards() { return this.client.download("/kd/standards/export"); }

  getNormativeImport(jobId: string): Promise<NormativeImport> { return this.client.request(`/kd/normative/imports/${jobId}`); }
  getNormativeCatalog(): Promise<NormativeCatalog> { return this.client.request("/kd/normative/catalog"); }
  getNormativeSources(snapshotId: string): Promise<NormativeSources> { return this.client.request(`/kd/normative/${snapshotId}/sources`); }
  getNormativeCards(snapshotId: string, filters: { source_id?: string; query?: string; decision?: string; card_type?: string; offset?: number; include_excluded?: boolean }): Promise<Page<NormativeCard>> {
    return this.client.request(`/kd/normative/${snapshotId}/cards`, { query: { ...filters, limit: 50 } });
  }
  getNormativeCard(snapshotId: string, externalId: string): Promise<NormativeDetail> { return this.client.request(`/kd/normative/${snapshotId}/cards/${externalId}`); }
  reviewNormativeCard(snapshotId: string, externalId: string, body: NormativeReviewCreate): Promise<NormativeReview> {
    return this.client.request(`/kd/normative/${snapshotId}/cards/${externalId}/reviews`, { method: "POST", body });
  }
  connectNormative(body: { snapshot_id: string; mode: "pilot" | "verified"; comment: string; expected_release_id: string | null; expected_review_token: string }): Promise<NormativeRelease> {
    return this.client.request("/kd/normative/connections", { method: "POST", body });
  }
  getNormativeGraph(snapshotId: string): Promise<KnowledgeGraphRead> { return this.client.request(`/kd/normative/${snapshotId}/graph`); }
  downloadNormativePage(snapshotId: string, sourceId: string, page: number) { return this.client.download(`/kd/normative/${snapshotId}/sources/${sourceId}/pages/${page}`); }
  downloadNormativeOriginal(snapshotId: string, sourceId: string) { return this.client.download(`/kd/normative/${snapshotId}/sources/${sourceId}/file`); }
  exportNormative(snapshotId: string) { return this.client.download(`/kd/normative/${snapshotId}/export`); }

  getKDNormativeConfig(): Promise<KDNormativeConfig> { return this.client.request("/kd/normative-config"); }
  listKDPackages(): Promise<KDPackage[]> { return this.client.request("/kd/packages"); }
  createKDPackage(title: string, reviewerId?: string): Promise<KDPackage> {
    return this.client.request("/kd/packages", { method: "POST", body: { title, reviewer_id: reviewerId || null } });
  }
  listKDRuns(packageId: string): Promise<KDRun[]> {
    return this.client.request(`/kd/packages/${packageId}/runs`);
  }
  getKDElmaConfig(): Promise<ElmaOptions> { return this.client.request("/kd/elma-config"); }
  listKDMaterials(offset = 0, activeOnly = false): Promise<MaterialVersion[]> { return this.client.request("/kd/materials", { query: { offset, active_only: activeOnly } }); }
  getKDMaterialVersion(id: string): Promise<MaterialVersion> { return this.client.request(`/kd/materials/${id}`); }
  getKDMaterialReview(id: string): Promise<MaterialCatalogReview> { return this.client.request(`/kd/materials/${id}/review`); }
  uploadKDMaterials(title: string, file: File, libraryId?: string): Promise<MaterialVersion> {
    const body = new FormData(); body.append("title", title); body.append("file", file); if (libraryId) body.append("library_id", libraryId);
    return this.client.request("/kd/materials", { method: "POST", body });
  }
  setKDMaterialState(id: string, status: "active" | "retired"): Promise<MaterialVersion> { return this.client.request(`/kd/materials/${id}/state`, { method: "POST", body: { status } }); }
  downloadKDMaterials(id: string) { return this.client.download(`/kd/materials/${id}/file`); }

  listKDSources(offset = 0, limit = 50, activeOnly = false, currentOnly = false, includeDrafts = false): Promise<ReviewSource[]> {
    return this.client.request("/kd/sources", { query: { offset, limit, active_only: activeOnly, latest_only: currentOnly, include_retired: !currentOnly, include_drafts: includeDrafts, include_inactive: !currentOnly } });
  }
  getKDSource(versionId: string): Promise<ReviewSource> { return this.client.request(`/kd/sources/${versionId}`); }
  getKDSourceFragment(versionId: string, locator: string, reference: string): Promise<import("./kdSourceTypes").SourceFragment> {
    return this.client.request(`/kd/sources/${versionId}/fragment`, { query: { locator, reference } });
  }
  listKDDefaultSources(): Promise<ReviewSource[]> { return this.client.request("/kd/sources/defaults"); }
  setKDSourceDefault(versionId: string, enabled: boolean): Promise<ReviewSource> {
    return this.client.request(`/kd/sources/${versionId}/default`, { method: "PUT", body: { use_by_default: enabled } });
  }
  uploadKDSource(metadata: SourceMetadata, files: File[], sourceId?: string): Promise<ReviewSource> {
    const body = new FormData();
    body.append("metadata", JSON.stringify(metadata));
    for (const file of files) body.append("files", file);
    if (sourceId) body.append("source_id", sourceId);
    return this.client.request("/kd/sources", { method: "POST", body });
  }
  setKDSourceState(versionId: string, status: "active" | "retired"): Promise<ReviewSource> {
    return this.client.request(`/kd/sources/${versionId}/state`, { method: "POST", body: { status } });
  }
  downloadKDSourceFile(versionId: string, fileId: string) { return this.client.download(`/kd/sources/${versionId}/files/${fileId}`); }
  saveKDSourceRules(versionId: string, payload: import("./kdSourceTypes").SourceRuleUpdate): Promise<ReviewSource> {
    return this.client.request(`/kd/sources/${versionId}/rules`, { method: "PUT", body: payload });
  }
  prepareKDSourceRules(versionId: string, expectedRevision: number): Promise<ReviewSource> {
    return this.client.request(`/kd/sources/${versionId}/rules/prepare`, { method: "POST", body: { expected_revision: expectedRevision } });
  }
  downloadKDSourceImage(versionId: string, fileId: string, ordinal: number) { return this.client.download(`/kd/sources/${versionId}/images/${fileId}/${ordinal}`); }

  createKDRun(packageId: string, files: File[], references: KDReference[], elmaContext?: ElmaContext, sourceVersionIds: string[] = [], materialVersionId?: string, allowDraftSources = false, engineeringChecks = false, options: { materialEntryIds?: string[]; normativeSearch?: boolean } = {}): Promise<KDRun> {
    const body = new FormData();
    for (const file of files) body.append("files", file);
    body.append("references", JSON.stringify(references));
    if (elmaContext !== undefined) body.append("elma_context", JSON.stringify(elmaContext));
    body.append("source_version_ids", JSON.stringify(sourceVersionIds));
    body.append("allow_draft_sources", String(allowDraftSources));
    body.append("engineering_checks", String(engineeringChecks));
    body.append("normative_search", String(options.normativeSearch ?? true));
    if (options.materialEntryIds !== undefined) body.append("material_entry_ids", JSON.stringify(options.materialEntryIds));
    if (materialVersionId) body.append("material_version_id", materialVersionId);
    return this.client.request(`/kd/packages/${packageId}/runs`, { method: "POST", body });
  }
  cancelKDRun(runId: string): Promise<KDRun> {
    return this.client.request(`/kd/runs/${runId}/cancel`, { method: "POST" });
  }
  reviewKD(runId: string, findingId: string, status: KDReviewStatus, comment: string): Promise<KDRun> {
    return this.client.request(`/kd/runs/${runId}/reviews`, { method: "POST", body: { finding_id: findingId, status, comment } });
  }
  downloadKDPage(runId: string, documentId: string, page: number) {
    return this.client.download(`/kd/runs/${runId}/documents/${documentId}/pages/${page}`);
  }
  downloadKDDocument(runId: string, documentId: string) {
    return this.client.download(`/kd/runs/${runId}/documents/${documentId}/file`);
  }
  exportKDRun(runId: string) { return this.client.download(`/kd/runs/${runId}/export`); }

  devAuth(bitrixUserId: number): Promise<SessionAuthResponse> {
    return this.client.request("/auth/dev", {
      method: "POST",
      body: { bitrix_user_id: bitrixUserId },
      auth: false,
    });
  }

  passwordLogin(email: string, password: string): Promise<SessionAuthResponse> {
    return this.client.request("/auth/login", {
      method: "POST",
      body: { email, password },
      auth: false,
    });
  }

  setPassword(newPassword: string, currentPassword?: string): Promise<void> {
    return this.client.request("/auth/password", {
      method: "PUT",
      body: {
        new_password: newPassword,
        current_password: currentPassword || null,
      },
    });
  }

  linkBitrix(payload: {
    domain: string;
    member_id: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  }): Promise<void> {
    return this.client.request("/auth/bitrix/link", { method: "POST", body: payload });
  }

  me(): Promise<SessionAuthResponse> {
    return this.client.request("/auth/me");
  }

  logout(): Promise<void> {
    return this.client.request("/auth/logout", { method: "POST" });
  }

  listConversations(params: {
    archived?: boolean;
    q?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<Page<ConversationRead>> {
    return this.client.request("/conversations", { query: params });
  }

  createConversation(payload: ConversationCreate): Promise<ConversationRead> {
    return this.client.request("/conversations", { method: "POST", body: payload });
  }

  getConversation(id: UUID): Promise<ConversationRead> {
    return this.client.request(`/conversations/${id}`);
  }

  updateConversation(id: UUID, payload: ConversationUpdate): Promise<ConversationRead> {
    return this.client.request(`/conversations/${id}`, { method: "PATCH", body: payload });
  }

  listMessages(id: UUID): Promise<import("./types").MessageRead[]> {
    return this.client.request(`/conversations/${id}/messages`);
  }

  sendMessage(id: UUID, payload: ChatRequest): Promise<ChatResponse> {
    return this.client.request(`/conversations/${id}/messages`, {
      method: "POST",
      body: payload,
    });
  }

  async uploadAttachment(id: UUID, file: File): Promise<AttachmentRead> {
    const form = new FormData();
    form.append("file", file);
    return this.client.request(`/conversations/${id}/attachments`, {
      method: "POST",
      body: form,
    });
  }

  setFeedback(messageId: UUID, payload: FeedbackCreate): Promise<StatusResponse> {
    return this.client.request(`/conversations/messages/${messageId}/feedback`, {
      method: "PUT",
      body: payload,
    });
  }

  shareConversation(
    id: UUID,
    userId: UUID,
    access: ConversationAccess,
  ): Promise<StatusResponse> {
    return this.client.request(`/conversations/${id}/members`, {
      method: "PUT",
      body: { user_id: userId, access },
    });
  }

  transferConversation(id: UUID, newOwnerId: UUID): Promise<ConversationRead> {
    return this.client.request(`/conversations/${id}/transfer`, {
      method: "POST",
      body: { new_owner_id: newOwnerId },
    });
  }

  listConversationMembers(id: UUID): Promise<ConversationMemberRead[]> {
    return this.client.request(`/conversations/${id}/members`);
  }

  removeConversationMember(id: UUID, userId: UUID): Promise<StatusResponse> {
    return this.client.request(`/conversations/${id}/members/${userId}`, {
      method: "DELETE",
    });
  }

  listDirectoryUsers(params: {
    q?: string;
    active?: boolean | null;
    limit?: number;
  } = {}): Promise<DirectoryUserRead[]> {
    return this.client.request("/directory/users", {
      query: {
        q: params.q,
        active: params.active,
        limit: params.limit ?? 100,
      },
    });
  }

  listImports(limit = 30, offset = 0): Promise<Page<IngestionJobRead>> {
    return this.client.request("/knowledge/imports", { query: { limit, offset } });
  }

  getImport(id: UUID): Promise<IngestionJobRead> {
    return this.client.request(`/knowledge/imports/${id}`);
  }

  getImportIssues(id: UUID): Promise<IngestionIssueRead[]> {
    return this.client.request(`/knowledge/imports/${id}/issues`);
  }

  uploadKnowledge(params: {
    mode: import("./types").ImportMode;
    files: File[];
    targetFolder?: string;
  }): Promise<IngestionUploadResponse> {
    const form = new FormData();
    form.append("mode", params.mode);
    if (params.targetFolder) form.append("target_folder", params.targetFolder);
    for (const file of params.files) form.append("files", file);
    return this.client.request("/knowledge/imports", {
      method: "POST",
      body: form,
    });
  }

  downloadImportFile(fileId: UUID): Promise<{ blob: Blob; filename: string | null }> {
    return this.client.download(`/knowledge/imports/files/${fileId}/download`);
  }

  listSnapshots(): Promise<KnowledgeSnapshotRead[]> {
    return this.client.request("/knowledge/snapshots");
  }

  publishSnapshot(id: UUID): Promise<KnowledgeSnapshotRead> {
    return this.client.request(`/knowledge/snapshots/${id}/publish`, { method: "POST" });
  }

  listCards(params: {
    snapshotId?: UUID | null;
    query?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<Page<KnowledgeCardSummary>> {
    return this.client.request("/knowledge/cards", {
      query: {
        snapshot_id: params.snapshotId,
        query: params.query,
        limit: params.limit ?? 30,
        offset: params.offset ?? 0,
      },
    });
  }

  getCard(externalId: string, snapshotId?: UUID | null): Promise<KnowledgeCardDetail> {
    return this.client.request(`/knowledge/cards/${encodeURIComponent(externalId)}`, {
      query: { snapshot_id: snapshotId },
    });
  }

  saveKnowledgeCardDraft(payload: KnowledgeCardDraftCreate): Promise<KnowledgeCardDraftRead> {
    return this.client.request("/knowledge/cards/drafts", { method: "POST", body: payload });
  }

  createKnowledgeCardRemoval(
    externalId: string,
    expectedVersionId: UUID,
  ): Promise<KnowledgeCardRemovalRead> {
    return this.client.request(
      `/knowledge/cards/${encodeURIComponent(externalId)}/removal-drafts`,
      { method: "POST", body: { expected_version_id: expectedVersionId } },
    );
  }

  getKnowledgeGraph(snapshotId?: UUID | null, limit = 2000): Promise<KnowledgeGraphRead> {
    return this.client.request("/knowledge/graph", {
      query: { snapshot_id: snapshotId, limit },
    });
  }

  listRetrievalProfiles(): Promise<RetrievalProfileRead[]> {
    return this.client.request("/retrieval-profiles");
  }

  getRetrievalProfile(id: UUID): Promise<RetrievalProfileDetailRead> {
    return this.client.request(`/retrieval-profiles/${id}`);
  }

  createRetrievalProfile(payload: RetrievalProfileCreate): Promise<RetrievalProfileRead> {
    return this.client.request("/retrieval-profiles", { method: "POST", body: payload });
  }

  updateRetrievalProfile(
    id: UUID,
    payload: RetrievalProfileUpdate,
  ): Promise<RetrievalProfileRead> {
    return this.client.request(`/retrieval-profiles/${id}`, {
      method: "PATCH",
      body: payload,
    });
  }

  debugRetrieval(query: string, profileId?: UUID | null): Promise<RetrievalDebugResponse> {
    return this.client.request("/retrieval-debug", {
      method: "POST",
      body: { query, profile_id: profileId ?? null },
    });
  }

  getRetrievalRun(id: UUID): Promise<RetrievalRunRead> {
    return this.client.request(`/retrieval-runs/${id}`);
  }

  listUsers(): Promise<UserAdminRead[]> {
    return this.client.request("/users");
  }

  createUser(payload: {
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    role_codes: string[];
  }): Promise<UserAdminRead> {
    return this.client.request("/users", { method: "POST", body: payload });
  }

  listRoles(): Promise<RoleRead[]> {
    return this.client.request("/users/roles");
  }

  setUserRoles(id: UUID, roleCodes: string[]): Promise<UserAdminRead> {
    return this.client.request(`/users/${id}/roles`, {
      method: "PUT",
      body: { role_codes: roleCodes },
    });
  }

  setUserStatus(id: UUID, isActive: boolean): Promise<UserAdminRead> {
    return this.client.request(`/users/${id}/status`, {
      method: "PUT",
      body: { is_active: isActive },
    });
  }
}
